/**
 * Noticing that the app was opened.
 *
 * The app is opened far more often than it is loaded. On the Home Screen iOS freezes the page
 * and hands it back on the next tap, so `load` fires once in the app's whole life and every
 * opening after that is a visibility change and nothing else. Anything that should happen when
 * somebody opens the app has to hang off this rather than off startup.
 */

/**
 * Calls `open` every time the app comes to the foreground, and returns a function that stops
 * watching. Not called for the first load, which the caller is already handling by being there.
 *
 * `open` has to tolerate being called twice for one opening: two events mean it, and neither is
 * reliable on its own.
 */
export function whenOpened(open: () => void): () => void {
  const opened = (): void => {
    if (document.visibilityState === 'visible') {
      open();
    }
  };

  // A resumed Home Screen app becomes visible; a page coming back from the back/forward cache
  // may not, and announces itself this way instead.
  const restored = (event: PageTransitionEvent): void => {
    if (event.persisted) {
      opened();
    }
  };

  document.addEventListener('visibilitychange', opened);
  window.addEventListener('pageshow', restored);

  return () => {
    document.removeEventListener('visibilitychange', opened);
    window.removeEventListener('pageshow', restored);
  };
}
